import React, { createContext, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { UseState } from "Types";
import * as yup from "yup";

import { useAppState } from "../../../../../../../../context/StateContext";
import { Member } from "../../../../../../../../models/members/member";
import { MemberAvailability } from "../../../../../../../../models/members/member.availability";
import { MemberCertification } from "../../../../../../../../models/members/member.certification";
import { MemberStaff } from "../../../../../../../../models/members/member.staff";
import { MemberTimesheet } from "../../../../../../../../models/members/member.timesheet";
import { MemberUser } from "../../../../../../../../models/members/member.user";
import { Filters, Searches, Sorts } from "../../../../../../../../models/shipeezi.table";
import MembersService from "../../../../../../../../services/members.service";
import { CACHE_TIME, STALE_TIME_OF_TWO_MINUTES } from "../../../../../../../../util/constants";
import { Status } from "../../../../../../../../util/enums";
import getRelatedFilters from "../../../../../../../../util/getRelatedFilters";
import { MemberKeys } from "../../../../../../../../util/queries";

interface AccountContextProps {
  memberHasShifted?: boolean;
  setMemberHasShifted?: (value: boolean) => void;
  member: MemberStaff | MemberUser;
  setMember: UseState<MemberStaff | MemberUser>;
  handleUpdateMember: (attribute: string, value: any) => void;
  certifications: MemberCertification[];
  handleUpdateCertifications: React.Dispatch<React.SetStateAction<MemberCertification[]>>;
  availabilities: MemberAvailability[];
  handleUpdateAvailabilities: React.Dispatch<React.SetStateAction<MemberAvailability[]>>;
  setTimesheets: React.Dispatch<React.SetStateAction<MemberTimesheet | null>>;
  timesheets: MemberTimesheet | null;

  timesheetErrors: yup.ValidationError[];
  setTimesheetErrors: React.Dispatch<React.SetStateAction<yup.ValidationError[]>>;

  pageSize: number;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  searches: Searches;
  setSearches: React.Dispatch<React.SetStateAction<Searches>>;
  filters?: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters | undefined>>;
  sorting?: Sorts;
  setSorting: React.Dispatch<React.SetStateAction<Sorts | undefined>>;
  membersList?: Member[];
  refetchMembers: () => void;
  isFetching: boolean;
}

const MemberContext = createContext<AccountContextProps>({} as AccountContextProps);

interface AccountProviderProps {
  children: React.ReactChild;
}

const MemberProvider = ({ children }: AccountProviderProps) => {
  const { currentUser } = useAppState();

  const [member, setMember] = useState<MemberStaff | MemberUser>({
    status: Status.ACTIVE,
    companyId: currentUser?.companyId,
  } as MemberStaff | MemberUser);

  const [certifications, setCertifications] = useState<MemberCertification[]>([]);
  const [availabilities, setAvailabilities] = useState<MemberAvailability[]>([]);
  const [timesheets, setTimesheets] = useState<MemberTimesheet | null>(null);
  const [memberHasShifted, setMemberHasShifted] = useState<boolean>(false);

  const [timesheetErrors, setTimesheetErrors] = useState<yup.ValidationError[]>([]);

  const [pageSize, setPageSize] = useState<number>(20);
  const [page, setPage] = useState<number>(0);
  const [searches, setSearches] = useState<Searches>([]);
  const [filters, setFilters] = useState<Filters>();
  const [sorting, setSorting] = useState<Sorts>();

  const {
    data: membersList,
    refetch: refetchMembers,
    isFetching,
  } = useQuery({
    queryKey: MemberKeys.getAll([page, pageSize, searches, sorting, filters, currentUser?.branches]),
    queryFn: async () => {
      const response = await MembersService.getAll({
        page,
        pageSize,
        searchByOr: searches?.length ? JSON.stringify(searches) : "",
        sortBy: sorting?.length ? JSON.stringify(sorting) : "",
        companyId: currentUser?.companyId,
        include: JSON.stringify([
          {
            association: "userCompany",
            include: [
              {
                association: "manager",
                attributes: ["id", "fullName"],
              },
              {
                association: "role",
                attributes: ["id", "name"],
                required: !!getRelatedFilters(filters, "role"),
                where: Object.values(filters ?? {}).length
                  ? getRelatedFilters(filters, "role")
                  : null,
              },
              {
                association: "applications",
                attributes: ["id", "name"],
                required: !!getRelatedFilters(filters, "application"),
                where: Object.values(filters ?? {}).length
                  ? getRelatedFilters(filters, "application")
                  : null,
              },
            ],
            required: !!(
              getRelatedFilters(filters, "role") || getRelatedFilters(filters, "application")
            ),
          },
          {
            association: "location",
            attributes: ["id", "longAddress"],
          },
          {
            association: "branches",
            attributes: ["id", "name", "companyId"],
            where: { id: currentUser?.branches?.map((branch) => branch.id), companyId: currentUser?.companyId },
            required: false,
          },
          {
            association: "user",
            attributes: ["id", "verified", "emailConfirmed"],
          },
        ]),
        ...getRelatedFilters(filters, "member"),
      });

      if (response?.data && Array.isArray(response.data)) return response.data;

      return [];
    },
    enabled: !!currentUser?.companyId,
    refetchOnWindowFocus: false,
    staleTime: STALE_TIME_OF_TWO_MINUTES,
    cacheTime: CACHE_TIME,
  });

  const handleUpdateMember = (attribute: string, value: any) => {
    setMember((state) => ({
      ...state,
      [attribute]: value,
    }));
  };

  return (
    <MemberContext.Provider
      value={{
        timesheetErrors,
        setTimesheetErrors,
        timesheets,
        setTimesheets,
        memberHasShifted,
        setMemberHasShifted,
        member,
        setMember,
        handleUpdateMember,
        certifications,
        handleUpdateCertifications: setCertifications,
        availabilities,
        handleUpdateAvailabilities: setAvailabilities,
        pageSize,
        setPageSize,
        page,
        setPage,
        searches,
        setSearches,
        filters,
        setFilters,
        sorting,
        setSorting,
        membersList,
        isFetching,
        refetchMembers,
      }}
    >
      {children}
    </MemberContext.Provider>
  );
};

export { MemberContext, MemberProvider };
